import React from "react";
import {
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  IconButton,
  Select,
} from "@chakra-ui/react";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import { NumberInputWithElement } from "components/NumberInputWithElement/NumberInputWithElement";
import { IEnvironmentalVariable } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { getUnitsMeasurement } from "util/getUnitsByEnvironmentalVariableId/getUnitsByEnvironmentalVariableId";
import i18n from "../../../i18n";
import { parseDecimal } from "util/input/parseDecimal";

interface ConditionItemProps {
  condition: {
    environmentalVariable: string;
    environmentalVariableId: number;
    comparisonOperator: string;
    value: number;
  };
  environmentalVariables?: IEnvironmentalVariable[];
  onUpdate: (field: string, value: any) => void;
  onRemove: () => void;
  canRemove: boolean;
  t: (key: string) => string;
  conditionIndex: number;
  groupIndex: number;
  errors?: any;
  /** Formik field name for the condition groups array. Defaults to "conditionGroups". */
  fieldArrayName?: string;
}

export const ConditionItem: React.FC<ConditionItemProps> = ({
  condition,
  environmentalVariables,
  onUpdate,
  onRemove,
  canRemove,
  errors,
  t,
  conditionIndex,
  groupIndex,
  fieldArrayName = "conditionGroups",
}) => {
  return (
    <Grid templateColumns="repeat(12, 1fr)" gap={6}>
      <GridItem colSpan={4}>
        <FormControl
          isRequired
          id={`${fieldArrayName}[${groupIndex}].conditions[${conditionIndex}].environmentalVariableId`}
        >
          <Select
            borderRadius={8}
            backgroundColor="#f0f1f2"
            fontSize={14}
            size="md"
            color="rgba(30, 82, 151, 0.8)"
            _focus={{ borderColor: "none" }}
            value={condition.environmentalVariableId}
            onChange={(e) =>
              onUpdate("environmentalVariableId", e.target.value)
            }
          >
            {environmentalVariables?.map((variable) => (
              <option value={variable.id} key={variable.id}>
                {variable.name}
              </option>
            ))}
          </Select>
        </FormControl>
      </GridItem>

      <GridItem colSpan={4}>
        <FormControl
          isRequired
          id={`${fieldArrayName}[${groupIndex}].conditions[${conditionIndex}].comparisonOperator`}
        >
          <Select
            borderRadius={8}
            backgroundColor="#f0f1f2"
            fontSize={14}
            size="md"
            color="rgba(30, 82, 151, 0.8)"
            _focus={{ borderColor: "none" }}
            value={condition.comparisonOperator}
            onChange={(e) => onUpdate("comparisonOperator", e.target.value)}
          >
            <option value=">">{t("isGreaterThan")}</option>
            <option value=">=">{t("isEqualToOrGreaterThan")}</option>
            <option value="=">{t("isEqualTo")}</option>
            <option value="<">{t("isLessThan")}</option>
            <option value="<=">{t("isEqualToOrLessThan")}</option>
          </Select>
        </FormControl>
      </GridItem>

      <GridItem colSpan={3}>
        <FormControl
          isRequired
          id={`${fieldArrayName}[${groupIndex}].conditions[${conditionIndex}].value`}
          isInvalid={
            errors?.[groupIndex]?.conditions?.[conditionIndex]?.value
          }
        >
          <NumberInputWithElement
            placeholder="0.0"
            inputStyle={{ backgroundColor: "#f0f1f2" }}
            suffix={getUnitsMeasurement(
              condition.environmentalVariableId,
              i18n.language as "pt-BR" | "en"
            )}
            value={condition.value}
            onChange={(e) => {
              onUpdate("value", parseDecimal(e.target.value));
            }}
          />
          <FormErrorMessage>{t("valueRequired")}</FormErrorMessage>
        </FormControl>
      </GridItem>

      <GridItem colSpan={1}>
        <IconButton
          layerStyle="lightRedOcean"
          _hover={{
            layerStyle: "lightRedOcean",
          }}
          icon={<DeleteOutline />}
          size="md"
          aria-label={t("removeCondition")}
          onClick={onRemove}
          isDisabled={!canRemove}
        />
      </GridItem>
    </Grid>
  );
};
